import { Link, useLocation } from "react-router-dom";
import { Fragment } from "react";
import { House } from "@phosphor-icons/react";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useBreadcrumbTrail } from "@/lib/breadcrumb";

const LABELS = {
  "": "Dashboard",
  knowledge: "Knowledge Hub",
  "compliance-ref": "Compliance Reference",
  library: "Content Library",
  proposals: "Proposal Assistant",
  grc: "GRC Assistant",
  soc: "SOC / MDR Assistant",
  cloud: "Cloud Operations",
  sales: "Sales & Marketing",
  executive: "Executive Assistant",
  training: "Training Assistant",
  governance: "AI Governance",
  studio: "Studio",
  insights: "Insights",
  outputs: "AI Outputs",
  workspaces: "Client Workspaces",
  pipeline: "Pipeline",
  pricing: "Pricing",
  "master-documents": "Master Documents",
  "control-library": "Control Library",
  "regulations": "Regulatory Mapping",
  frameworks: "Frameworks",
  "policy-analyzer": "Policy Analyzer",
  intelligence: "Global Intelligence",
  forecast: "Posture Forecast",
  "firm-dna": "Firm DNA Health",
  "reuse-library": "Reuse Library",
  radar: "Threat Radar",
  analytics: "Analytics",
  "review-queue": "Output Approvals",
  audit: "Audit Logs",
  security: "Security & Compliance",
  admin: "Administration",
  users: "Users",
  roles: "Roles & Permissions",
  environment: "Environment Settings",
  platform: "Platform Settings",
  templates: "Templates & Branding",
  data: "Data Management",
  workflow: "Workflow & Approvals",
  integrations: "Integrations",
  maintenance: "System Maintenance",
  settings: "Settings",
};

const HomeCrumb = ({ current = false }) =>
  current ? (
    <BreadcrumbPage className="flex items-center gap-1.5"><House className="h-3.5 w-3.5" weight="bold" /> Dashboard</BreadcrumbPage>
  ) : (
    <BreadcrumbLink asChild><Link to="/" className="flex items-center gap-1.5"><House className="h-3.5 w-3.5" weight="bold" /> Home</Link></BreadcrumbLink>
  );

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const trail = useBreadcrumbTrail();

  // Custom, entity-aware trail published by a detail page (e.g. Clients › Acme Corp).
  if (trail && trail.length) {
    return (
      <Breadcrumb data-testid="breadcrumbs">
        <BreadcrumbList className="font-mono text-[11px] uppercase tracking-wide">
          <BreadcrumbItem><HomeCrumb /></BreadcrumbItem>
          {trail.map((item, i) => {
            const isLast = i === trail.length - 1;
            return (
              <Fragment key={`${item.label}-${i}`}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {isLast || !item.to ? (
                    <BreadcrumbPage className="max-w-[28ch] truncate">{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild><Link to={item.to}>{item.label}</Link></BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Fallback: derive the trail from the URL path.
  const segments = pathname.split("/").filter(Boolean);
  return (
    <Breadcrumb data-testid="breadcrumbs">
      <BreadcrumbList className="font-mono text-[11px] uppercase tracking-wide">
        <BreadcrumbItem><HomeCrumb current={segments.length === 0} /></BreadcrumbItem>
        {segments.map((seg, i) => {
          const to = "/" + segments.slice(0, i + 1).join("/");
          const isLast = i === segments.length - 1;
          const label = LABELS[seg] || (["workspaces", "outputs"].includes(segments[i - 1]) ? "Detail" : seg.replace(/-/g, " "));
          return (
            <Fragment key={to}>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild><Link to={to}>{label}</Link></BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
